"use client";
import { useEffect, useState } from "react";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb.client";
import ICMWrapper from "@/app/components/icm-wrapper";
import {
    INVESTOR_NOTICE_URL,
    INVESTOR_REPORT_URL,
    INVESTOR_REPORT_URL2,
    INVESTOR_NOTICE_URL2,
    INVESTOR_NOTICE_URL3,
    INVESTOR_ORDER_URL,
    INVESTOR_MEETING_URL,
    INVESTOR_MEETING_URL1,
    INVESTOR_MEETING_URL2,
    INVESTOR_TAB_URL,
    INVESTOR_TAB_URL1,
    INVESTOR_TAB_URL2,
    INVESTOR_TAB_URL3,
    INVESTOR_TAB_URL4,
    INVESTOR_TAB_URL5,
    INVESTOR_MEETING_URL3
} from "@/app/utils/constants";

interface DropdownOption {
    value: string;
    label: string;
    url: string;
}

interface DropdownConfig {
    title: string;
    options: DropdownOption[];
}

const InvestorRelations = () => {
    const dropdownOptions: DropdownConfig[] = [
        {
            title: "Annual reports",
            options: [
                {
                    value: "annual",
                    label: "Annual report 23-24 Alpha Fintech",
                    url: INVESTOR_REPORT_URL,
                },
                {
                    value: "annual1",
                    label: "Annual report 24-25 Alpha Fintech",
                    url: INVESTOR_REPORT_URL2,
                },
            ],
        },
        {
            title: "Financial Tab",
            options: [
                {
                    value: "tab",
                    label: "Alpha Fintech Private Limited Consolidated 2022-23",
                    url: INVESTOR_TAB_URL,
                },
                {
                    value: "tab1",
                    label: "Alpha Fintech Private Limited Consolidated 2023-24",
                    url: INVESTOR_TAB_URL1,
                },
                {
                    value: "tab2",
                    label: "Alpha Fintech Private Limited Consolidated 2024-25",
                    url: INVESTOR_TAB_URL2,
                },
                {
                    value: "tab3",
                    label: "Alpha Fintech Private Limited Standalone 2022-23",
                    url: INVESTOR_TAB_URL3,
                },
                {
                    value: "tab4",
                    label: "Alpha Fintech Private Limited Standalone 2023-24",
                    url: INVESTOR_TAB_URL4,
                },
                {
                    value: "tab5",
                    label: "Alpha Fintech Private Limited Standalone 2024-25",
                    url: INVESTOR_TAB_URL5,
                },
            ],
        },
        {
            title: "NCLT Convened Meetings",
            options: [
                {
                    value: "meeting",
                    label: "Form CAA 2 - Notice of meeting of ESH",
                    url: INVESTOR_MEETING_URL,
                },
                {
                    value: "meeting1",
                    label: "Alpha Fintech - Notice to unsecured creditors",
                    url: INVESTOR_MEETING_URL1,
                },
                {
                    value: "meeting2",
                    label: "Form CAA 2 - Newspaper publication for meeting of ESH",
                    url: INVESTOR_MEETING_URL2,
                },
                {
                    value: "meeting3",
                    label: "Scrutinisers Report",
                    url: INVESTOR_MEETING_URL3,
                },
            ],
        },
        {
            title: "Notices",
            options: [
                {
                    value: "notice",
                    label: "Notice of Annual General Meeting-23-24",
                    url: INVESTOR_NOTICE_URL,
                },
                {
                    value: "notice1",
                    label: "Notice of Annual General Meeting-24-25",
                    url: INVESTOR_NOTICE_URL2,
                },
                {
                    value: "notice2",
                    label: "InCred Techinvest EGM Notice_October 05, 2026",
                    url: INVESTOR_NOTICE_URL3,
                },
            ],
        },
        {
            title: "Orders",
            options: [
                {
                    value: "order",
                    label: "Admission Order",
                    url: INVESTOR_ORDER_URL,
                },
            ],
        },
    ];

    useEffect(() => {
        setAnalyticsEvent("/investor-relation", "investor-relation");
    }, []);

    const handleRedirection = (url: string): void => {
        window.open(url, "_blank", "noopener");
    };

    // Keep track of selected values for each dropdown
    const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});

    const handleChange = (title: string, val: string, options: DropdownOption[]) => {
        const selectedOption = options.find((option) => option.value === val);
        if (selectedOption) {
            handleRedirection(selectedOption.url);
        }
        // Reset so user can re-click same option again
        setSelectedValues((prev) => ({ ...prev, [title]: "" }));
    };

    const investorBreadcrumbPaths: BreadcrumbPath[] = [
        { label: "Home", route: "/" },
        { label: "Investor Relations" },
    ];

    return (
        <ICMWrapper>
            <div className="w-full md:px-36">
            <Breadcrumb paths={investorBreadcrumbPaths} />
            <div className="flex flex-col pb-24 pt-6 md:items-start md:pb-[160px] md:pt-20">
                <h1 className="text-4xl font-semibold leading-[120%] tracking-[-0.48px] md:text-6xl">
                    Investor Relations
                </h1>

                <div className="flex w-full flex-col justify-between gap-6 pt-s7 md:flex-row md:gap-14 md:pt-14">
                    {dropdownOptions.map(({ title, options }) => (
                        <Select
                            key={title}
                            value={selectedValues[title] || ""}
                            onValueChange={(val) => handleChange(title, val, options)}
                        >
                            <SelectTrigger className="h-[66px] rounded-xl px-s8 text-left text-lg font-regular leading-[140%] [&>svg]:h-6 [&>svg]:w-6 [&>svg]:text-[color:var(--neutral-700)] [&>svg]:opacity-100 whitespace-nowrap">
                                <SelectValue placeholder={title} />
                            </SelectTrigger>

                            <SelectContent className="rounded-xl [&>*]:p-0">
                                {options.map((option) => (
                                    <SelectItem
                                        className="truncate h-[66px] rounded-xl md:text-lg text-[13px] font-regular leading-[140%] whitespace-nowrap overflow-hidden text-ellipsis"
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ))}
                </div>
            </div>
            </div>
        </ICMWrapper>
    );
};

export default InvestorRelations;
